import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import Text from '@/components/ui/Text';
import { plato_api_multi_correct_grading_attrs_question_path } from '@/modules/routes';
import { canViewCorrectAnswers, noAnswerProvided, renderCorrectnessIcon } from '@/components/staffer/Grading/utils';
import { StudentAnswerProps } from '../types';

export const multipleChoiceQuestionAnswerAttributes = ['is_correct', 'selected_choices_text', 'gather_attempts'];

const StudentAttemptTableCell = ({ answerOptionIndex, attemptIndex, question, questionAnswer }) => {
  const [isCorrect, setIsCorrect] = useState<boolean>();

  const isCorrectQuery = useQuery(['isCorrect', question.id, attemptIndex, answerOptionIndex], () => (
    Axios.get(plato_api_multi_correct_grading_attrs_question_path(question.id,
      { choice_index: answerOptionIndex,
        question_answer_id: questionAnswer.id }))
      .then(res => res.data)
  ));

  useEffect(() => {
    if (isCorrectQuery.isLoading) return;
    setIsCorrect(isCorrectQuery.data.is_choice_correct);
  }, [isCorrectQuery.data]);

  if (!isCorrectQuery.isLoading && isCorrectQuery.data) {
    return <td>{renderCorrectnessIcon(isCorrect)}</td>;
  }
  return <td>Loading...</td>;
};

const MultipleChoiceQuestionStudentAnswer = ({ question, questionAnswer }: StudentAnswerProps) => {
  const showCorrectAnswers = canViewCorrectAnswers();

  const multiAttemptMultipleChoice = () => {
    const studentAttempts = answerOptionIndex => (
      [0, 1].map((attemptIndex) => {
        // the current answer option is the same answer option the user has selected for this attempt
        if (questionAnswer.gather_attempts[attemptIndex] === answerOptionIndex.toString()) {
          return (
            <StudentAttemptTableCell
              key={`${question.id}-${attemptIndex}`}
              answerOptionIndex={answerOptionIndex}
              attemptIndex={attemptIndex}
              question={question}
              questionAnswer={questionAnswer}
            />
          );
        }
        return <td key={`${question.id}-${attemptIndex}`} />;
      })
    );

    const studentAnswerTable = () => (
      question.answer_choices.filter(Boolean) // remove blanks
        .map((answerOption, answerOptionIndex) => (
          <tr key={`${question.id}-${answerOptionIndex}`}>
            <td>{answerOption}</td>
            {studentAttempts(answerOptionIndex)}
            <td>
              {
                question.correct_choices.includes(answerOptionIndex.toString())
                && <i aria-hidden className="fa fa-check" />
              }
            </td>
          </tr>
        ))
    );

    return (
      <table className="multi-correct-answer-preview answer-preview-table">
        <thead>
          <tr>
            <td>Answer Choices</td>
            <td>Selected Answer (1st)</td>
            <td>Selected Answer (2nd)</td>
            <td>Correct Selections</td>
          </tr>
        </thead>
        <tbody>
          {studentAnswerTable()}
        </tbody>
      </table>
    );
  };

  if (!questionAnswer) return noAnswerProvided();

  if (questionAnswer.gather_attempts?.length !== 0) {
    return multiAttemptMultipleChoice();
  }

  return (
    <table className="multiple-choice-answer-preview answer-preview-table tw-min-w-[29em]">
      <thead>
        <tr>
          <Text asChild size="sm" weight="bold">
            <th className="tw-p-2">Answer Choice</th>
          </Text>
          <Text asChild size="sm" weight="bold">
            <th>Selected Answer</th>
          </Text>
          {showCorrectAnswers && (
            <Text asChild size="sm" weight="bold">
              <th>Correct Selection</th>
            </Text>
          )}
        </tr>
      </thead>
      <tbody>
        {question.answer_choices.filter(Boolean).map((answerChoice, i) => {
          const index = i.toString();
          const answerChoiceIsCorrect = question.correct_choices.includes(index);
          const answers = questionAnswer.answer.split(',');
          const isCorrect = answerChoiceIsCorrect && answers.includes(index);

          return (
            <tr className="tw-text-center">
              <td className="tw-align-middle">
                <Text size="sm" weight="bold">
                  {answerChoice}
                </Text>
              </td>
              <td>
                {answers.includes(index) && renderCorrectnessIcon(isCorrect, '!tw-text-2xl')}
              </td>
              {showCorrectAnswers && (
                <td>
                  {answerChoiceIsCorrect && (
                    <i aria-hidden className="fa fa-check-circle !tw-text-2xl" />
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default MultipleChoiceQuestionStudentAnswer;
